/* eslint-disable */
/* eslint-disable no-console */
import { CancelToken, CancelTokenSource } from 'axios';
import React, {
  PropsWithChildren,
  createContext,
  useState,
  useEffect,
  useRef,
  useCallback,
  useContext,
  ReactNode,
  useMemo
} from 'react';

import { getCancelTokenSource } from '../../api/client';
import { getStoreConfigs, getStoreDetailByNumber, getUserPreferences, setUserPreferences } from '../../api/user';
import { SessionStorageKeys } from '../../config/storage-config';
import { CustomerSkipTraceRequestLevelsType, StoreConfigsResponse, StoreDetail } from '../../types/types';
import { getSelectedStore } from '../../utils/utils';

export interface StoreState {
  isFranchisee: boolean;
  selectedStore?: string;
  storeGoalsSelectedDate: Date;
  allStores: any[];
  setSelectedStore?: (storeId: string) => void;
  setAutoPreferences: (value: boolean) => void;
  saveToPreferences: (storeId: string, cancelToken?: CancelToken) => void;
  setAllStores: (stores: any) => void;
  setStoreGoalsSelectedDate: (date: Date) => void;
}

export const ProductType = 'RPDDAP';

export const StoreContext = createContext<StoreState>({} as StoreState);

export const StoreProvider = ({ children }: PropsWithChildren<{}>) => {
  const [allStores, setAllStores] = useState<any>([]);
  const [isFranchisee, setIsFranchisee] = useState(false);
  const [storeGoalsSelectedDate, setStoreGoalsSelectedDate] = useState<Date>(
    new Date()
  );

  const [selectedStore, setSelectedStoreState] = useState('');
  const [autoPreferences, setAutoPreferences] = useState(true);
  const initialCallToken = useRef<CancelTokenSource>();

  useEffect(() => {
    if (!selectedStore || !allStores?.length) return;

    const store = allStores.find(
      (storeObj: any) => storeObj.storeNum === selectedStore
    );

    if (!store) {
      return;
    }
    setIsFranchisee(store.franchiseeStore);
  }, [allStores, selectedStore]);

  useEffect(() => {
    getUserPreferences(ProductType).then(preferences => {
      if (preferences) {
        const lastViewedStore = preferences.filter(
          (item: any) => item.productType === ProductType
        )[0]?.preferences?.lastViewedStore;
        if (lastViewedStore) {
          sessionStorage.setItem(
            SessionStorageKeys.STORE_NUMBER,
            lastViewedStore
          );
          setSelectedStoreState(lastViewedStore);
        }
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const saveToPreferences = (storeId: string, cancelToken?: CancelToken) => {
    sessionStorage.setItem(
      SessionStorageKeys.STORE_NUMBER,
      storeId // looks like a storeNumber
    );

    setUserPreferences(
      ProductType,
      {
        op: 'add',
        path: '/lastViewedStore',
        value: storeId
      },
      cancelToken
    );
  };

  const setSelectedStore = (storeId: string) => {
    setSelectedStoreState(storeId);
    if (autoPreferences) {
      if (initialCallToken.current) {
        initialCallToken.current.cancel();
      }
      initialCallToken.current = getCancelTokenSource();
      saveToPreferences(storeId, initialCallToken.current.token);
    }
  };

  // A user can be temporarily assigned to a store.
  // If the given store is saved in the coworker's preferences,
  // but removed from it's store list, the selected store is expected to fallback to the first store from the list.
  const handleCaseWhenPreferredStoreHasBeenRemoved = useCallback(() => {
    if (!allStores?.length) {
      return;
    }

    setSelectedStore(allStores[0].storeNum);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allStores]);

  useEffect(() => {
    if (!selectedStore || !allStores?.length) return;

    const store = allStores.find(
      (storeObj: any) => storeObj.storeNum === selectedStore
    );

    if (!store) {
      handleCaseWhenPreferredStoreHasBeenRemoved();
    }
  }, [allStores, handleCaseWhenPreferredStoreHasBeenRemoved, selectedStore]);

  return (
    <StoreContext.Provider
      value={{
        selectedStore,
        setSelectedStore,
        setAutoPreferences,
        saveToPreferences,
        allStores,
        setAllStores,
        isFranchisee,
        storeGoalsSelectedDate,
        setStoreGoalsSelectedDate
      }}
    >
      {children}
    </StoreContext.Provider>
  );
};
//

//


export interface StoreState {
  store?: StoreDetail;
  loading: boolean;
  hasApiError: boolean;
  isAddressDoctorEnabled: boolean;
  isRetentionEnabled: boolean;
  referenceContactInterval: number;
  contactWeekLimit: number;
  isExchangeEnabled: boolean;
  exchangePeriod: string;
  isGoogleAddressValidationEnabled: boolean;
  isExchangeFuncEnabled: boolean;
  rewardsEligibilityAmount: number;
  customerRewards: boolean;
  customerVerificationOptimization: boolean;
  isLMDEnabled: boolean;
  enableFieldVisitStrategy: boolean;
  maxReceiptsEmailable: number;
  isCustomerRecordCheckEnabled: boolean;
  customerDataUnmaskingThresholdDays: number;
  customerSkipTraceRequestLevels: CustomerSkipTraceRequestLevelsType;
  isMXAccountManagement: boolean;
  MXAccountMgmtAccess: number;
  customerRecordCheckRequestThresholdDays: number;
  potentialRecordCheck: number[];
  textLanguage: string;
  MXAddressValidation: boolean;
  enableAddressValidation: number | undefined;
  dateTimeLocalization: number | undefined;
  countryIdentification: string | undefined;
  supportPhoneNumber:string;
}

const NO_LIMIT = 9999;
const NO_LIMIT_STRING = "-1";
const NO_INTERVAL_LIMIT = 0;

export interface StoreDispatchState {
  fetchStoreDetailsByNumber: (
    supportPhoneNumber: string,
    cancelToken: CancelTokenSource
  ) => Promise<void | StoreDetail[]>;
}

export const checkIfAddressDoctorEnabled = (
  response: StoreConfigsResponse[]
): boolean => {
  const addressDoctor = response?.[0]?.configDetails?.find((config) => {
    return (
      config.paramGroupName === "AddressDoctorEnabledIndicator" &&
      config.paramCategoryName === "AddressDoctorEnabled" &&
      config.paramKeyName === "AddressDoctorEnabled"
    );
  });
  if (addressDoctor && addressDoctor.paramValue === "1") {
    return true;
  }
  return false;
};

export const getLMDEnabled = (response: StoreConfigsResponse[]): boolean => {
  const lmd = response?.[0]?.configDetails?.find((config) => {
    return config.paramKeyName === "EnableLMD";
  });
  if (lmd && lmd.paramValue === "1") {
    return true;
  }
  return false;
};

export const shouldCreateFieldVisit = (response: StoreConfigsResponse[]): boolean => {
  const fv = response?.[0]?.configDetails?.find((config) => {
    return config.paramKeyName === "EnableFieldVisitStrategy";
  });
  if (fv && fv.paramValue === "1") {
    return true;
  }
  return false;
};
export const getMXAccountManagement = (
  response: any[]
): boolean => {
  const mxAccManage = response?.data[0]?.configDetails?.find((config) => {
    return config.paramKeyName === "MXAccountManagement";
  });
  if (mxAccManage && mxAccManage.paramValue === "1") {
    return true;
  }
  return false;
};

export const checkIfRetentionEnabled = (
  response: StoreConfigsResponse[]
): boolean => {
  const retentionResponse = response?.[0]?.configDetails?.find((config) => {
    return (
      config.paramGroupName === "PaymentAccess" &&
      config.paramCategoryName === "SubSystemAccess" &&
      config.paramKeyName === "CustomerRetention"
    );
  });
  if (retentionResponse && retentionResponse.paramValue === "1") {
    return true;
  }
  return false;
};

export const getAgreementOpenPeriod = (
  response: StoreConfigsResponse[]
): string => {
  const exchangeResponse = response?.[0]?.configDetails?.find(
    (config) => config.paramKeyName === "AgreementExchangePeriod"
  );
  if (exchangeResponse && exchangeResponse.paramValue) {
    return exchangeResponse.paramValue;
  }
  return "";
};
export const checkIfExchangeEnabled = (
  response: StoreConfigsResponse[]
): boolean => {
  const exchangeResponse = response?.[0]?.configDetails?.find(
    (config) => config.paramKeyName === "AgreementExchange"
  );
  if (exchangeResponse && exchangeResponse.paramValue === "1") {
    return true;
  }
  return false;
};
export const getContactWeekLimit = (
  response: StoreConfigsResponse[]
): number => {
  const configResp = response?.[0]?.configDetails?.find((config) => {
    return (
      config.paramGroupName === "CommunicationLimit" &&
      config.paramCategoryName === "AccountManagement" &&
      config.paramKeyName === "CustomerContactLimitPastWeek"
    );
  });
  if (configResp && configResp.paramValue !== NO_LIMIT_STRING) {
    return Number(configResp.paramValue);
  }
  return NO_LIMIT;
};

export const checkIsAddressValidationEnabled = (
  response: StoreConfigsResponse[]
) => {
  const exchangeResponse = response?.[0]?.configDetails?.find(
    (config) => config.paramKeyName === "EnableGoogleAddressValidation"
  );
  if (exchangeResponse && exchangeResponse.paramValue === "1") {
    return true;
  }
  return false;
};
export const checkIsMXAddressValidationEnabled = (
  response: StoreConfigsResponse[]
) => {
  const exchangeResponse = response?.[0]?.configDetails?.find(
    (config) => config.paramKeyName === "MXAddressValidation"
  );
  if (exchangeResponse && exchangeResponse.paramValue === "1") {
    return true;
  }
  return false;
};
export const getReferenceContactInterval = (
  response: StoreConfigsResponse[]
): number => {
  const configResp = response?.[0]?.configDetails?.find((config) => {
    return (
      config.paramGroupName === "CommunicationLimit" &&
      config.paramCategoryName === "AccountManagement" &&
      config.paramKeyName === "ReferenceContactInterval"
    );
  });
  if (configResp && configResp.paramValue !== NO_LIMIT_STRING) {
    return Number(configResp.paramValue);
  }
  return NO_INTERVAL_LIMIT;
};

export const checkIsExchangeFuncEnabled = (
  response: StoreConfigsResponse[]
) => {
  const exchangeResponse = response?.[0]?.configDetails?.find(
    (config) => config.paramKeyName === "RACExchange"
  );
  if (exchangeResponse && exchangeResponse.paramValue === "1") {
    return true;
  }
  return false;
};

export const CustomerRewards = (response: StoreConfigsResponse[]): boolean => {
  const exchangeResponse = response?.[0]?.configDetails?.find(
    (config) => config.paramKeyName === "CustomerRewards"
  );
  if (exchangeResponse && exchangeResponse.paramValue === "1") {
    return true;
  }
  return false;
};
export const CustomerVerificationOptimization = (
  response: StoreConfigsResponse[]
): boolean => {
  const exchangeResponse = response?.[0]?.configDetails?.find(
    (config) => config.paramKeyName === "CustomerVerificationOptimization"
  );
  if (exchangeResponse && exchangeResponse.paramValue === "1") {
    return true;
  }
  return false;
};
export const RewardsEligibilityAmount = (
  response: StoreConfigsResponse[]
): number => {
  const configResp = response?.[0]?.configDetails?.find((config) => {
    return config.paramKeyName === "RewardsEligibilityAmount";
  });
  if (configResp && configResp.paramValue !== NO_LIMIT_STRING) {
    return Number(configResp.paramValue);
  }
  return NO_INTERVAL_LIMIT;
};

export const getMaxReceiptsEmailable = (
  response: StoreConfigsResponse[]
): number => {
  const maxReceipts = response?.[0]?.configDetails?.find((config) => {
    return config.paramKeyName === "MaxReceiptsEmailable";
  });
  // if maxReceipts is undefined, NaN || 0 will still be 0
  return Number(maxReceipts?.paramValue) || 0;
};

export const CustomerRecordCheckAccess = (
  response: StoreConfigsResponse[]
): boolean => {
  const configResp = response?.[0]?.configDetails?.find(
    (config) => config.paramKeyName === 'EnableCustomerRecordCheckPhase2'
  );
  if (configResp && configResp.paramValue === "1") {
    return true;
  }
  return false;
};

export const CustomerDataUnmaskingThresholdDays = (
  response: StoreConfigsResponse[]
): number => {
  const configResp = response?.[0]?.configDetails?.find(
    (config) => config.paramKeyName === "CustomerDataUnmaskingThresholdDays"
  );
  return Number(configResp?.paramValue) || 0;
};

export const MXAccountManagementAccess = (
  response: StoreConfigsResponse[]
): number => {
  const configResp = response?.[0]?.configDetails?.find(
    (config) => config.paramKeyName === 'MXAccountManagement'
  );
  return Number(configResp?.paramValue) || 0;
};

export const CustomerSkipTraceRequestLevels = (
  response: StoreConfigsResponse[]
): Record<string, number[]> => {
  const configResp = response?.[0]?.configDetails?.find(
    (config) => config.paramKeyName === "CustomerSkipTraceRequestLevels"
  );
  return configResp?.paramValue
    ? JSON.parse(configResp.paramValue.replace(/[“”]/g, '"'))
    : { NW: [1, 2, 3, 4, 5] };
};

export const CustomerRecordCheckRequestThresholdDays = (
  response: StoreConfigsResponse[]
): number => {
  const configResp = response?.[0]?.configDetails?.find(
    (config) => config.paramKeyName === 'CustomerRecordCheckRequestThresholdDays'
  );
  return Number(configResp?.paramValue) || 0;
};

export const EnableAddressValidation = (
  response: StoreConfigsResponse[]
): any => {
  const configResp = response?.[0]?.configDetails?.find(
    (config) => config.paramKeyName === 'EnableAddressValidation'
  );
  return Number(configResp?.paramValue) || 0;
};

export const DateTimeLocalization = (
  response: StoreConfigsResponse[]
): any => {
  const configResp = response?.data[0]?.configDetails?.find(
    (config) => config.paramKeyName === 'DateTimeLocalization'
  );
  return Number(configResp?.paramValue) || 0;
};

export const SupportPhoneNumber = (
  response: any
): any => {
  const configResp = response?.data?.[0]?.configDetails?.find(
    (config) => config.paramKeyName === 'SupportContactNumber'
  );

  return (configResp?.paramValue) || 0;
};

export interface StoreDispatchState {
  fetchStoreDetailsByNumber: (storeNumber: string, cancelToken: CancelTokenSource) => Promise<void | StoreDetail[]>;
}

// [Helper functions (checkIfRetentionEnabled, getLMDEnabled, etc.) remain the same as provided]

export const StoreStateContext = createContext<StoreState>({} as StoreState);
export const StoreDispatchContext = createContext<StoreDispatchState>({} as StoreDispatchState);

export const StoreProviders = ({ children }: { children: ReactNode }) => {
  const [store, setStore] = useState<StoreDetail>();
  const [loading, setLoading] = useState<boolean>(false);
  const [hasApiError, setHasApiError] = useState(false);
  const [isAddressDoctorEnabled, setIsAddressDoctorEnabled] = useState(false);
  const [isRetentionEnabled, setIsRetentionEnabled] = useState(false);
  const [isExchangeEnabled, setIsExchangeEnabled] = useState(false);
  const [exchangePeriod, setExchangePeriod] = useState<string>("");
  const [contactWeekLimit, setContactWeekLimit] = useState(NO_LIMIT);
  const [isGoogleAddressValidationEnabled, setIsGoogleAddressValidationEnabled] = useState<boolean>(false);
  const [isExchangeFuncEnabled, setIsExchangeFuncEnabled] = useState<boolean>(false);
  const [referenceContactInterval, setReferenceContactInterval] = useState(NO_LIMIT);
  const [rewardsEligibilityAmount, setRewardsEligibilityAmount] = useState(NO_LIMIT);
  const [customerRewards, setCustomerRewards] = useState<boolean>(false);
  const [customerVerificationOptimization, setCustomerVerificationOptimization] = useState<boolean>(false);
  const [isLMDEnabled, setIsLMDEnabled] = useState(false);
  const [enableFieldVisitStrategy, setEnableFieldVisitStrategy] = useState(false);
  const [maxReceiptsEmailable, setMaxReceiptsEmailable] = useState(0);
  const [isCustomerRecordCheckEnabled, setIsCustomerRecordCheckEnabled] = useState(false);
  const [customerDataUnmaskingThresholdDays, setCustomerDataUnmaskingThresholdDays] = useState(0);
  const [customerSkipTraceRequestLevels, setCustomerSkipTraceRequestLevels] = useState<Record<string, number[]>>({});
  const [isMXAccountManagement, setIsMXAccountManagement] = useState(false);
  const [MXAccountMgmtAccess, setMXAccountMgmtAccess] = useState(0);
  const [customerRecordCheckRequestThresholdDays, setCustomerRecordCheckRequestThresholdDays] = useState(0);
  const [potentialRecordCheck, setPotentialRecordCheck] = useState<number[]>([]);
  const [textLanguage, setTextLanguage] = useState<string>("");
  const [MXAddressValidation, setMXAddressValidation] = useState(false);
  const [enableAddressValidation, setEnableAddressValidation] = useState<number | undefined>(undefined);
  const [supportPhoneNumber,setSupportPhoneNumber]=useState<string>('');
  const [dateTimeLocalization, setDateTimeLocalization] = useState();
  const [countryIdentification, setCountryIdentification] = useState(); 
   useEffect(() => {
   const fetchcountryDetails = async (storeNumber: string) => {
    try {
      const details = await getStoreDetailByNumber(storeNumber);
      const country = details?.response[0]?.countryAbb?.toString().toUpperCase?.();
      setCountryIdentification(country || '');
    } catch (error: any) {
      console.log('something went wrong', error);
    }
  };

  const fetchStoreDetails = async () => {
    const storeDetails = sessionStorage.getItem('storeDetails');
    if (storeDetails) {
      try {
        const decoded = Buffer.from(storeDetails, 'base64').toString('utf8');
        const parsed = JSON.parse(decoded);
        const country = parsed?.country?.toString().toUpperCase();
        setCountryIdentification(country || '');
        return; 
      } catch (error: any) {
      console.log('something went wrong', error);
    }
    }
    else{
       const storeNumber = sessionStorage.getItem('storeNumber');
        if (!storeNumber) {
          return;
        }
       await fetchcountryDetails(storeNumber);
    }
  };
  fetchStoreDetails();
}, []);

  const fetchStoreConfig = (selectedStore: string, cancelToken: CancelTokenSource) => {
    const paramKeyName = [
      'AddressDoctorEnabled', 'CustomerRetention', 'CustomerContactLimitPastWeek', 'ReferenceContactInterval',
      'AgreementExchange', 'AgreementExchangePeriod', 'EnableGoogleAddressValidation', 'RACExchange',
      'RewardsEligibilityAmount', 'CustomerRewards', 'Txt2PayCardOnFile', 'CustomerVerificationOptimization',
      'EnableLMD', 'EnableFieldVisitStrategy', 'MaxReceiptsEmailable', 'EnableCustomerRecordCheckPhase2',
      'CustomerDataUnmaskingThresholdDays', 'CustomerSkipTraceRequestLevels', 'MXAccountManagement',
      'CustomerRecordCheckRequestThresholdDays', 'MXAddressValidation', 'EnableAddressValidation',
      'SupportContactNumber','DateTimeLocalization'
    ];
    getStoreConfigs({ storeNumbers: [selectedStore], paramKeyNames: paramKeyName }, cancelToken.token)
      .then((response) => {
        setIsRetentionEnabled(checkIfRetentionEnabled(response));
        setIsAddressDoctorEnabled(checkIfAddressDoctorEnabled(response));
        setContactWeekLimit(getContactWeekLimit(response));
        setReferenceContactInterval(getReferenceContactInterval(response));
        setIsExchangeEnabled(checkIfExchangeEnabled(response));
        setExchangePeriod(getAgreementOpenPeriod(response));
        setIsGoogleAddressValidationEnabled(checkIsAddressValidationEnabled(response));
        setMXAddressValidation(checkIsMXAddressValidationEnabled(response));
        setIsExchangeFuncEnabled(checkIsExchangeFuncEnabled(response));
        setRewardsEligibilityAmount(RewardsEligibilityAmount(response));
        setCustomerRewards(CustomerRewards(response));
        setCustomerVerificationOptimization(CustomerVerificationOptimization(response));
        const value = getMXAccountManagement(response);
        setIsMXAccountManagement(value);
        setTextLanguage(value ? 'S' : 'en');
        setIsLMDEnabled(getLMDEnabled(response));
        setEnableFieldVisitStrategy(shouldCreateFieldVisit(response));
        setMaxReceiptsEmailable(getMaxReceiptsEmailable(response));
        setIsCustomerRecordCheckEnabled(CustomerRecordCheckAccess(response));
        setCustomerDataUnmaskingThresholdDays(CustomerDataUnmaskingThresholdDays(response));
        setCustomerSkipTraceRequestLevels(CustomerSkipTraceRequestLevels(response));
        setMXAccountMgmtAccess(MXAccountManagementAccess(response));
        setCustomerRecordCheckRequestThresholdDays(CustomerRecordCheckRequestThresholdDays(response));
        setEnableAddressValidation(EnableAddressValidation(response));
        setDateTimeLocalization(DateTimeLocalization(response));
        setSupportPhoneNumber(SupportPhoneNumber(response));
      })
      .catch((error) => {
        setHasApiError(true);
      });
  };

  const fetchStoreDetailsByNumber = async (storeNumber: string, cancelToken: CancelTokenSource) => {
    setHasApiError(false);
    setLoading(true);
    try {
      const response = await getStoreDetailByNumber(storeNumber, cancelToken.token);
      if (response?.response?.length) setStore(response.response[0]);
    } catch (error) {
      setHasApiError(true);
    } finally {
      setLoading(false);
    }
  };

  const selectedStore = useMemo(() => getSelectedStore(), []);
  useEffect(() => {
    const cancelToken = getCancelTokenSource();
    fetchStoreDetailsByNumber(selectedStore, cancelToken);
    fetchStoreConfig(selectedStore, cancelToken);
    return () => cancelToken.cancel();
  }, [selectedStore]);

  return (
    <StoreStateContext.Provider
      value={{
        store,
        loading,
        hasApiError,
        isAddressDoctorEnabled,
        isRetentionEnabled,
        referenceContactInterval,
        contactWeekLimit,
        isExchangeEnabled,
        exchangePeriod,
        isGoogleAddressValidationEnabled,
        isExchangeFuncEnabled,
        rewardsEligibilityAmount,
        customerRewards,
        customerVerificationOptimization,
        isLMDEnabled,
        enableFieldVisitStrategy,
        maxReceiptsEmailable,
        isCustomerRecordCheckEnabled,
        customerDataUnmaskingThresholdDays,
        customerSkipTraceRequestLevels,
        isMXAccountManagement,
        MXAccountMgmtAccess,
        customerRecordCheckRequestThresholdDays,
        potentialRecordCheck,
        textLanguage,
        MXAddressValidation,
        enableAddressValidation,
        dateTimeLocalization,
        countryIdentification,
        supportPhoneNumber
      }}
    >
      <StoreDispatchContext.Provider value={{ fetchStoreDetailsByNumber }}>
        {children}
      </StoreDispatchContext.Provider>
    </StoreStateContext.Provider>
  );
};

export const useStoreDetails = () => useContext(StoreStateContext);
export const useStoreActions = () => useContext(StoreDispatchContext);